import _ from 'lodash';
import * as Sentry from '@sentry/browser';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { ToastContainer } from 'react-toastify';
import { Alert } from 'react-bootstrap';
import PropTypes from 'prop-types';
import DetectRTC from 'detectrtc';
import Api from '../services/ApiService';
import Socket from '../services/SocketService';
import browserHistory from '../history';
import ModalConductor from './modal/ModalConductor';
import ChatConductor from './chat/ChatConductor';
import { getInfo } from '../actions/AppActions';
import LoadingPage from './LoadingPage';
import AuthService from '../services/AuthService';
import { modal } from './modal/ModalEmitter';
import ChangeLog from './ChangeLog';

const openAbout = () => {
  if (!window.navigator.webdriver) {
    // don't show for automated tests
    modal.info({ title: "What's New?", message: <ChangeLog />, style: 'center', view: 'about', size: 'md' });
  }
};

class App extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isReady: false,
      showVersionAlert: false,
    };

    this.version = null;

    // indicates if rules and timers have already started
    this.versionTransitionStarted = false;

    this.currentTheme = '';
  }

  componentDidMount() {
    // prevent running while testing to avoid warning spam
    if (!/HeadlessChrome/.test(window.navigator.userAgent)) {
      // detect video/audio capabilities
      DetectRTC.load();
    }

    Socket.global.on('version', version => {
      this.version = version;
      if (this.props.version && this.props.version !== version) {
        this.startVersionTransition();
      }
    });

    // Get config, user data, and page search data
    this.props.getInfo().then(() => {
      if (this.props.user.disabled) {
        AuthService.logout();
      }

      Sentry.setTags({
        blazeVersion: this.props.version,
      });

      Sentry.setUser({
        username: this.props.user.username,
        email: this.props.user.email,
      });

      if (this.version && this.props.version !== this.version) {
        this.startVersionTransition();
      }

      if (this.props.user.username && this.props.version !== _.get(this.props.user, 'data.versionAck')) {
        Api.put('user/data', {
          key: 'versionAck',
          data: this.props.version,
        });

        setTimeout(() => {
          openAbout();
        }, 50);
      }

      this.setState({ isReady: true });
    });

    this.timer = setInterval(() => {
      if (document.visibilityState === 'hidden') {
        return;
      }

      if (!this.state.showVersionAlert) {
        Api.get('version').then(res => {
          if (_.get(res.data, 'version') !== this.props.version) {
            this.startVersionTransition();
          }
        });
      }
    }, 1000 * 60 * 5);
  }

  componentDidUpdate() {
    if (document.body) {
      const { branch } = this.props;
      const theme = branch.theme ? `${branch.theme}-theme` : 'default-theme';

      const { classList } = document.body;

      if (classList.contains(this.currentTheme)) {
        classList.remove(this.currentTheme);
      }

      classList.add(theme);
      this.currentTheme = theme;
    }
  }

  componentWillUnmount() {
    if (this.timer) {
      clearInterval(this.timer);
    }

    Socket.global.off('version');
  }

  startVersionTransition() {
    if (window.location.pathname.startsWith('/terminal')) {
      return;
    }
    this.setState({ showVersionAlert: true });

    if (this.versionTransitionStarted) {
      return;
    }

    this.versionTransitionStarted = true;

    // reload page on route change if version mismatch detected
    browserHistory.listen(() => {
      window.location.reload(true);
    });

    // give Cletus an hour to do it himself, then force it, per Ben
    setTimeout(() => {
      window.location.reload(true);
    }, 1000 * 60 * 60);
  }

  renderVersionAlert() {
    if (!this.state.showVersionAlert) {
      return null;
    }

    return (
      <Alert id="version-alert" variant="warning">
        <i className="zmdi zmdi-alert-circle-o zmdi-hc-lg" />
        <strong>New Version Available!</strong>{' '}
        <a
          onClick={() => {
            window.location.reload(true);
          }}
        >
          Click Here
        </a>{' '}
        to start using it or refresh the page.
      </Alert>
    );
  }

  render() {
    if (!this.state.isReady) {
      return <LoadingPage />;
    }

    const CloseButton = ({ closeToast }) => (
      <div className="toast-close-icon" onClick={closeToast}>
        <i className="zmdi zmdi-close zmdi-hc-2x" />
      </div>
    );

    return (
      <div id="app">
        {this.renderVersionAlert()}
        {this.props.children}
        <ToastContainer
          // hideProgressBar
          bodyClassName="toast-body"
          toastClassName="toast-container"
          closeButton={<CloseButton />}
        />
        <ModalConductor />
        <ChatConductor />
      </div>
    );
  }
}

App.defaultProps = {
  branch: {},
  user: {},
};

App.propTypes = {
  branch: PropTypes.object,
  children: PropTypes.object,
  getInfo: PropTypes.func,
  user: PropTypes.object,
  version: PropTypes.string,
};

function mapDispatchToProps(dispatch) {
  return bindActionCreators({ getInfo }, dispatch);
}

function mapStateToProps(state) {
  return {
    version: _.get(state.app, 'config.version'),
    user: state.app.user,
    branch: state.app.branch,
  };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(App));
